import {
  IndexedInteractionStepResponse,
  InteractionStepConfig,
  InteractionStepType,
  StepParamsForType,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { ReviewLevel } from '@scale/shared-types';
import { IMultiTurnThreadHistory } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ThreadHistory.interface';
import TextCollectionViewComponent from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/TextCollection/TextCollectionViewComponent';
import TextCollectionPinnedViewComponent from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/TextCollection/TextCollectionPinnedViewComponent';

import tcGetResponseTemplatePaths from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/TextCollection/tcGetResponseTemplatePaths';
import {
  createDefaultResponseForFields,
  isEmptyResponse,
} from '@scale/shared/textcollection/helpers';
import { isConditionallyVisible, isStepVisible } from '@scale/shared/chat/api';
import QualityMeasurementConfigurationComponent from './QualityMeasurementConfigurationComponent';
import { Types } from '@scale/scaleui';
import { schema } from './validation';
import StepDescriptor from '../stepDescriptor/StepDescriptor';
import { registerStepDescriptor } from '../stepDescriptor/stepDescriptorContainer';

export class QualityMeasurementDescriptor extends StepDescriptor<InteractionStepType.QualityMeasurement> {
  type = InteractionStepType.QualityMeasurement;
  title = InteractionStepConfig[InteractionStepType.QualityMeasurement].title;
  description = InteractionStepConfig[InteractionStepType.QualityMeasurement].description;

  validationSchema = schema;

  ViewComponent = TextCollectionViewComponent;
  PinnedViewComponent = TextCollectionPinnedViewComponent;
  ConfigurationComponent = QualityMeasurementConfigurationComponent;

  isResponseValid(
    response: IndexedInteractionStepResponse,
    stepParams: StepParamsForTypeWithStage<InteractionStepType.QualityMeasurement>,
    history: IMultiTurnThreadHistory,
    reviewLevel?: ReviewLevel,
  ) {
    if (
      !isStepVisible(stepParams, reviewLevel) ||
      !isConditionallyVisible(
        stepParams,
        history.responses,
        response.index,
        history.threadParams.templateVariables,
        reviewLevel,
      )
    ) {
      return { canSubmit: true, submitTooltip: '' };
    }

    if (isEmptyResponse(response.context.response)) {
      return { canSubmit: false, submitTooltip: 'Some steps have not been completed.' };
    }

    return { canSubmit: true, submitTooltip: '' };
  }

  getResponseTemplatePaths(
    stepParams: StepParamsForType<InteractionStepType.QualityMeasurement>,
    _history: IMultiTurnThreadHistory,
  ) {
    return tcGetResponseTemplatePaths(stepParams.params.fields);
  }

  generateEmptyResponse(
    stepParams: StepParamsForType<InteractionStepType.QualityMeasurement>,
    history: IMultiTurnThreadHistory,
  ) {
    const emptyResponse = createDefaultResponseForFields(stepParams.params.fields);
    return {
      type: InteractionStepType.QualityMeasurement,
      context: {
        response: emptyResponse,
      },
      output: history.getMostRecentResponse(),
    };
  }

  getConfigurationPreviewTags(
    stepParams: StepParamsForType<InteractionStepType.QualityMeasurement>,
  ) {
    const tags = super.getConfigurationPreviewTags(stepParams);
    const newTags: Types.Tag[] = [
      {
        value: 'Standardized and Restricted',
        variant: 'warning',
        icon: 'lock',
        tooltipText: `Internal quality measurement step, standardized and restricted (only allows removing questions)`,
      },
      ...tags,
    ];
    return newTags;
  }
}

registerStepDescriptor({
  type: InteractionStepType.QualityMeasurement,
  descriptor: new QualityMeasurementDescriptor(),
});
